import type { SelectChangeEvent } from "@mui/material/Select";
import { RoundingUnitKind } from "@sinch/core/entities/serverEnums";
import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRateRounding } from "@sinch/components/application/RateRounding/useRateRounding";
import { useRoundingLabels } from "@sinch/components/application/RateRounding/useRoundingLabels";
import { SelectInput } from "@sinch/components/form/SelectInput/SelectInput";

interface RoundingSelectInputProps {
  value: string | null | undefined;
  onChange: (roundingId: string | null) => void;
  /** Only definitions of this kind are offered */
  unitKind: RoundingUnitKind;
  /** Floating label of the select, omit when a surrounding section label already names the field */
  label?: string;
  /** Name of what applies when nothing is picked, rendered as "Use default (name)" */
  inheritedLabel?: string | null;
  /** Label of the empty option, takes precedence over `inheritedLabel` when set */
  emptyLabel?: string;
  disabled?: boolean;
  /** Render the menu inside the select's DOM subtree, needed inside click-away popovers */
  disablePortal?: boolean;
  "data-cy"?: string;
}

/**
 * Select of rounding definitions of one unit kind with an "inherit" option
 * - archived definitions are hidden unless they are the current value
 */
export const RoundingSelectInput: FC<RoundingSelectInputProps> = ({
  value,
  onChange,
  unitKind,
  label,
  inheritedLabel,
  emptyLabel,
  disabled,
  disablePortal,
  ...rest
}) => {
  const { t } = useTranslation();
  const { roundings } = useRateRounding();
  const { formatRounding } = useRoundingLabels();

  const options = useMemo(
    () => [
      {
        value: "",
        label:
          emptyLabel ??
          (inheritedLabel ? t("RateRounding.inheritNamed", { name: inheritedLabel }) : t("RateRounding.inherit")),
      },
      ...roundings
        .filter((rounding) => rounding.unitKind === unitKind && (!rounding.archived || rounding.id === value))
        .map((rounding) => ({ value: rounding.id, label: `${rounding.name} (${formatRounding(rounding)})` })),
    ],
    [emptyLabel, formatRounding, inheritedLabel, roundings, t, unitKind, value]
  );

  return (
    <SelectInput
      {...rest}
      disabled={disabled}
      disablePortal={disablePortal}
      displayEmpty
      fullWidth
      label={label}
      onChange={(event: SelectChangeEvent<unknown>) => {
        const selected = event.target.value;
        onChange(selected === "" || selected == null ? null : String(selected));
      }}
      options={options}
      value={value ?? ""}
    />
  );
};
